import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Modal,
  Select,
  Typography,
} from 'antd';
import {
  RefreshAssetEod,
  instrumentTypes,
  refreshAssetEodFromRefinitiv,
  selectors,
  uploadBulkEodPrices,
} from 'state';
import { useDispatch, useSelector } from 'react-redux';

const YIELD_TEMPLATE_ASSET_CLASSES = new Set(['AFSBond', 'HTMBond']);

export const buildPriceUploadTemplate = (assetClass: string) => {
  const ric = `YOUR_${assetClass.toUpperCase()}_RIC`;
  const includesYields = YIELD_TEMPLATE_ASSET_CLASSES.has(assetClass);
  const rows = includesYields
    ? [
        'RIC,PriceCloseDate,PriceClose,bid_yield,ask_yield',
        `${ric},01-25-2030,99.25,4.51,4.50`,
        `${ric},01-26-2030,99.40,4.49,4.48`,
      ]
    : [
        'RIC,PriceCloseDate,PriceClose',
        `${ric},01-25-2030,100.00`,
        `${ric},01-26-2030,101.25`,
      ];

  return {
    content: [...rows, ''].join('\n'),
    filename: `${assetClass}_price_upload_template.csv`,
  };
};

type Props = {
  hide: () => void;
};
const RefreshAssetEodModal: React.FC<Props> = ({ hide }) => {
  const dispatch = useDispatch();
  const waiting = useSelector(selectors.waiting);
  const [form] = Form.useForm();
  const assets = useSelector(selectors.underlyingAssetsList);
  const [refreshLoading, setRefreshLoading] = useState(false);

  const [data, setData] = useState(new FormData());
  const [fileSelected, setFileSelected] = useState(false);
  const [templateAssetClass, setTemplateAssetClass] = useState<string>();

  const priceUploadTemplate = templateAssetClass
    ? buildPriceUploadTemplate(templateAssetClass)
    : undefined;
  const priceUploadTemplateHref = priceUploadTemplate
    ? `data:text/csv;charset=utf-8,${encodeURIComponent(
        priceUploadTemplate.content,
      )}`
    : undefined;

  useEffect(() => {
    if (waiting) {
      setRefreshLoading(true);
    } else {
      setRefreshLoading(false);
    }
  }, [waiting]);

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.files as FileList;
    const formData = new FormData();
    if (raw[0]) formData.append('eod_file', raw[0], raw[0].name);
    setData(formData);
    setFileSelected(true);
  };

  const uploadHandler = async () => {
    const file = data.get('eod_file') as File;

    function readFileAsText(file: File) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
        reader.readAsText(file);
      });
    }

    const fileContent = await readFileAsText(file);
    console.log('fileContent: ', fileContent);
    console.log('typeof fileContent: ', typeof fileContent);
    dispatch(uploadBulkEodPrices({ eod_file: fileContent }));
  };

  return (
    <Modal
      open={true}
      onOk={form.submit}
      onCancel={hide}
      title={'Refresh Asset EoD data'}
      // footer={[{}]}
      width={600}
      footer={null}
    >
      <div>
        {/*<Row>*/}
        {/*<Col span={14}>*/}
        <div style={{ position: 'relative', padding: 10 }}>
          <Form
            form={form}
            labelCol={{ span: 6 }}
            // onFinish={() => {
            //   const data: RefreshIndexFund = {
            //     from_date: form.getFieldValue('from_date'),
            //     to_date: form.getFieldValue('to_date'),
            //     index_fund_id: form.getFieldValue('index_fund_id'),
            //     delete_prev_data: form.getFieldValue('delete_prev_data'),
            //   };
            //
            //   console.log(data);
            //   // dispatch(refreshIndexFundDataFromRefinitiv(data));
            //   // hide();
            // }}
            initialValues={{ delete_prev_data: false }}
          >
            <Form.Item
              label="From"
              name="from_date"
              rules={[{ required: true, message: 'required' }]}
            >
              <DatePicker />
            </Form.Item>
            <Form.Item
              label="To"
              name="to_date"
              rules={[{ required: true, message: 'required' }]}
            >
              <DatePicker />
            </Form.Item>
            <Form.Item
              label="Assets"
              name="asset_ids"
              rules={[{ required: true, message: 'required' }]}
            >
              <Select
                mode="multiple"
                showSearch
                allowClear
                optionFilterProp="children"
              >
                {assets.map(asset => {
                  return (
                    <Select.Option key={asset.id} value={asset.id}>
                      {asset.ric}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
            <Form.Item
              label="Delete prev. data"
              name="delete_prev_data"
              valuePropName="checked"
            >
              <Checkbox />
            </Form.Item>
            <div>
              (choosing this option for an FxForward asset will also delete
              previous data of corresponding FX, then recreate it.)
            </div>
          </Form>
          <Button
            key="key"
            loading={refreshLoading}
            // type="primary"
            style={{ position: 'absolute', right: 25, bottom: 0 }}
            onClick={async () => {
              try {
                const values = await form.validateFields();
                const selectedAssetIds = values.asset_ids as number[];
                const data: RefreshAssetEod = {
                  from_date: values.from_date,
                  to_date: values.to_date,
                  delete_prev_data: values.delete_prev_data,
                };

                if (selectedAssetIds.length === 1) {
                  data.asset_id = selectedAssetIds[0];
                } else {
                  data.asset_ids = selectedAssetIds;
                }

                dispatch(refreshAssetEodFromRefinitiv(data));
              } catch (error) {
                // Form validation already displays the relevant error state.
              }
            }}
          >
            Pull new data from Refinitiv
          </Button>
          {refreshLoading && (
            <Typography.Text
              type={'secondary'}
              style={{ position: 'absolute', right: 25, bottom: -25 }}
            >
              This may take a while, but you can safely close this modal.
            </Typography.Text>
          )}
        </div>
        <Divider>
          {<Typography.Text type={'secondary'}>or upload</Typography.Text>}
        </Divider>
        <form id={'eod_form'}>
          {/*<Popover*/}
          {/*  placement={'right'}*/}
          {/*  // content={infoHover}*/}
          {/*  title=".csv header must look like:"*/}
          {/*>*/}
          {/*  <InfoCircleOutlined />*/}
          {/*</Popover>*/}
          <Button
            disabled={!fileSelected}
            style={{ marginLeft: 10 }}
            onClick={uploadHandler}
          >
            Upload
          </Button>
          <input
            style={{ marginLeft: '8px', marginBottom: '12px' }}
            type="file"
            id="eod_file"
            name="eod_file"
            accept=".csv"
            onChange={e => handleFileSelection(e)}
          />
        </form>
        <div
          style={{
            margin: '12px 10px 0',
            paddingTop: 16,
          }}
        >
          <Typography.Text strong>Download a template</Typography.Text>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginTop: 8,
            }}
          >
            <Select
              aria-label="Template asset class"
              placeholder="Select asset class"
              showSearch
              optionFilterProp="label"
              style={{ minWidth: 220 }}
              value={templateAssetClass}
              onChange={setTemplateAssetClass}
              options={instrumentTypes.map(instrument => ({
                label: instrument.type,
                value: instrument.type,
              }))}
            />
            <Typography.Link
              aria-label="Download price upload template"
              disabled={!priceUploadTemplate}
              href={priceUploadTemplateHref}
              download={priceUploadTemplate?.filename}
            >
              Download template
            </Typography.Link>
          </div>
        </div>
        <Typography.Text
          type="secondary"
          style={{ display: 'block', padding: '8px 10px 10px' }}
        >
          AFSBond and HTMBond templates include bid and ask yields. All other
          asset classes use the standard price template.
        </Typography.Text>
        {/*<Divider style={{ marginTop: 45 }} />*/}
        {/*/!*</Col>*!/*/}
        {/*/!*<Col style={{ borderLeft: '2px solid #eeeeee' }} span={10}>*!/*/}
        {/*<div style={{ padding: 10 }}>*/}
        {/*  <form id={'news_form'}>*/}
        {/*    <Popover*/}
        {/*      placement={'right'}*/}
        {/*      content={infoHover}*/}
        {/*      title=".csv header must look like:"*/}
        {/*    >*/}
        {/*      <InfoCircleOutlined />*/}
        {/*    </Popover>*/}
        {/*    <Button*/}
        {/*      disabled={!fileSelected}*/}
        {/*      style={{ marginLeft: 10 }}*/}
        {/*      onClick={uploadHandler}*/}
        {/*    >*/}
        {/*      Upload*/}
        {/*    </Button>*/}
        {/*    <input*/}
        {/*      style={{ marginLeft: '8px', marginBottom: '12px' }}*/}
        {/*      type="file"*/}
        {/*      id="news_file"*/}
        {/*      name="news_file"*/}
        {/*      accept=".csv"*/}
        {/*      onChange={e => handleFileSelection(e)}*/}
        {/*    />*/}
        {/*  </form>*/}
        {/*</div>*/}
        {/*</Col>*/}
        {/*</Row>*/}
      </div>
    </Modal>
  );
};
export default RefreshAssetEodModal;
